import { API_BASE_URL } from "@/config";
import { getAccessToken } from "@/api/token";
import { queryClient, qk } from "./query";

/**
 * Server-Sent Events subscription for real-time updates (PRD FE-REALTIME).
 *
 * The native EventSource API cannot set Authorization headers, so we read the
 * stream with fetch + a ReadableStream parser, sending `Authorization: Bearer`.
 * The token is NEVER placed in the query string.
 *
 * Reconnects with exponential backoff; heartbeat comment lines (`: ...`) are
 * ignored. Incoming events invalidate the affected resource caches. An event
 * that is already being processed is de-duplicated by name+type.
 */

export type RealtimeEventType =
  | "memo.created"
  | "memo.updated"
  | "memo.attachments.updated"
  | "memo.deleted"
  | "memo.comment.created"
  | "reaction.upserted"
  | "reaction.deleted";

export interface RealtimeEvent {
  type: RealtimeEventType;
  /** Full resource name, e.g. "memos/{memo}". */
  name: string;
}

type EventHandler = (event: RealtimeEvent) => void;

const handlers = new Set<EventHandler>();
const recentEvents = new Map<string, number>();
const DEDUP_WINDOW_MS = 2_000;

function eventKey(e: RealtimeEvent): string {
  return `${e.type}:${e.name}`;
}

function isDuplicate(e: RealtimeEvent): boolean {
  const key = eventKey(e);
  const now = Date.now();
  const last = recentEvents.get(key);
  if (last !== undefined && now - last < DEDUP_WINDOW_MS) return true;
  recentEvents.set(key, now);
  // Opportunistic cleanup.
  if (recentEvents.size > 200) {
    for (const [k, t] of recentEvents) if (now - t > DEDUP_WINDOW_MS * 5) recentEvents.delete(k);
  }
  return false;
}

/** Default cache invalidation for a realtime event. */
function invalidateForEvent(e: RealtimeEvent): void {
  if (!e.name) return;
  queryClient.invalidateQueries({ queryKey: qk.memo(e.name) });
  // Lists are keyed by many filter dimensions; invalidate the whole collection.
  queryClient.invalidateQueries({ queryKey: ["memos"] });
  if (e.type === "memo.comment.created") {
    queryClient.invalidateQueries({ queryKey: qk.memoComments(e.name) });
  }
  if (e.type === "reaction.upserted" || e.type === "reaction.deleted") {
    queryClient.invalidateQueries({ queryKey: qk.memoReactions(e.name) });
  }
}

function dispatch(e: RealtimeEvent): void {
  if (isDuplicate(e)) return;
  invalidateForEvent(e);
  handlers.forEach((h) => h(e));
}

export function onRealtimeEvent(handler: EventHandler): () => void {
  handlers.add(handler);
  return () => handlers.delete(handler);
}

// ---------------------------------------------------------------------------
// Connection management
// ---------------------------------------------------------------------------

let abortController: AbortController | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempts = 0;
let running = false;

const MIN_BACKOFF_MS = 1_000;
const MAX_BACKOFF_MS = 30_000;

function backoff(): number {
  const exp = Math.min(MAX_BACKOFF_MS, MIN_BACKOFF_MS * 2 ** reconnectAttempts);
  // Add a little jitter to avoid thundering herd on reconnect.
  return exp * (0.8 + Math.random() * 0.4);
}

/** Parse an SSE stream, dispatching `data:` payloads. Ignores comment lines. */
async function readStream(res: Response): Promise<void> {
  if (!res.body) throw new Error("SSE response has no body");
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  const processEvent = (raw: string) => {
    const dataLines: string[] = [];
    for (const line of raw.split("\n")) {
      if (line.startsWith(":")) return; // heartbeat / comment
      if (line.startsWith("data:")) dataLines.push(line.slice(5).trimStart());
    }
    if (dataLines.length === 0) return;
    const data = dataLines.join("\n");
    try {
      const parsed = JSON.parse(data) as { type?: RealtimeEventType; name?: string };
      if (parsed.type && parsed.name) {
        dispatch({ type: parsed.type, name: parsed.name });
      }
    } catch {
      // Non-JSON payloads are ignored.
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const rawEvent = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      processEvent(rawEvent);
    }
  }
}

async function connect(): Promise<void> {
  if (!running) return;
  const token = getAccessToken();
  if (!token) {
    // No session yet; retry shortly (sign-in may be in progress).
    scheduleReconnect();
    return;
  }

  abortController = new AbortController();
  try {
    const res = await fetch(`${API_BASE_URL}/api/v1/sse`, {
      headers: { Authorization: `Bearer ${token}`, Accept: "text/event-stream" },
      credentials: "include",
      signal: abortController.signal,
    });
    if (!res.ok) {
      if (res.status === 401) {
        // Token problem; the auth layer will refresh. Back off and retry.
        scheduleReconnect();
        return;
      }
      throw new Error(`SSE connect failed: ${res.status}`);
    }
    reconnectAttempts = 0; // Connected successfully.
    await readStream(res);
    // Stream ended cleanly (server closed); reconnect.
    if (running) scheduleReconnect();
  } catch {
    if (abortController?.signal.aborted) return; // Intentional stop.
    if (running) scheduleReconnect();
  }
}

function scheduleReconnect(): void {
  if (!running) return;
  if (reconnectTimer) clearTimeout(reconnectTimer);
  const delay = backoff();
  reconnectAttempts += 1;
  reconnectTimer = setTimeout(() => void connect(), delay);
}

export function startRealtime(): void {
  if (running) return;
  running = true;
  reconnectAttempts = 0;
  void connect();
}

export function stopRealtime(): void {
  running = false;
  if (reconnectTimer) clearTimeout(reconnectTimer);
  reconnectTimer = null;
  abortController?.abort();
  abortController = null;
}

/** Called when the page returns from background to verify the connection. */
export function resumeRealtime(): void {
  if (!running) return;
  // If the underlying fetch died while hidden, kick a reconnect.
  abortController?.abort();
  reconnectAttempts = 0;
  void connect();
}
